<template>
  <ServicePageLayout
    icon="fas fa-hands-helping"
    title="Community & Social Involvement"
    subtitle="Making a difference through service and community engagement"
  >
    <!-- Overview -->
    <section class="content-section">
      <h2>
        <i class="fas fa-info-circle section-icon" />
        Overview
      </h2>
      <p>
        At Northeastern College, we believe in developing socially responsible citizens who are
        committed to making a positive impact in their communities. Our Community and Social
        Involvement programs provide students with meaningful opportunities to serve, learn,
        and grow through volunteerism and outreach activities.
      </p>
    </section>

    <!-- Volunteerism -->
    <section
      id="volunteerism"
      class="content-section"
    >
      <h2>
        <i class="fas fa-hand-holding-heart section-icon" />
        Volunteerism Programs
      </h2>
      <p>
        Volunteering allows students to contribute their time and skills to help others while
        developing valuable life skills and perspectives.
      </p>
      <div class="programs-grid">
        <div class="program-card">
          <div class="card-icon green">
            <i class="fas fa-book-reader" />
          </div>
          <h3>Tutorial Assistance</h3>
          <p>Help elementary and high school students with their academics.</p>
        </div>
        <div class="program-card">
          <div class="card-icon yellow">
            <i class="fas fa-tree" />
          </div>
          <h3>Environmental Initiatives</h3>
          <p>Tree planting, clean-up drives, and environmental awareness campaigns.</p>
        </div>
        <div class="program-card">
          <div class="card-icon green">
            <i class="fas fa-home" />
          </div>
          <h3>Habitat Projects</h3>
          <p>Assist in building or renovating homes for underprivileged families.</p>
        </div>
        <div class="program-card">
          <div class="card-icon yellow">
            <i class="fas fa-heartbeat" />
          </div>
          <h3>Health Missions</h3>
          <p>Medical and dental missions in underserved communities.</p>
        </div>
      </div>
    </section>

    <section class="content-section highlight">
      <h2>
        <i class="fas fa-users section-icon" />
        Community Outreach Programs
      </h2>
      <p>
        Our outreach programs connect students directly with communities that need support,
        fostering empathy and social awareness.
      </p>
      <div class="outreach-list">
        <div class="outreach-item">
          <div class="outreach-icon">
            <i class="fas fa-gift" />
          </div>
          <div class="outreach-content">
            <h4>Annual Gift-Giving Program</h4>
            <p>
              Holiday sharing activities bringing joy to children in orphanages, elderly care
              homes, and disadvantaged communities.
            </p>
          </div>
        </div>
        <div class="outreach-item">
          <div class="outreach-icon yellow">
            <i class="fas fa-utensils" />
          </div>
          <div class="outreach-content">
            <h4>Feeding Programs</h4>
            <p>
              Regular feeding activities for malnourished children and families in need.
            </p>
          </div>
        </div>
        <div class="outreach-item">
          <div class="outreach-icon">
            <i class="fas fa-hands" />
          </div>
          <div class="outreach-content">
            <h4>Disaster Response</h4>
            <p>
              Relief operations and assistance during calamities and emergencies.
            </p>
          </div>
        </div>
        <div class="outreach-item">
          <div class="outreach-icon yellow">
            <i class="fas fa-graduation-cap" />
          </div>
          <div class="outreach-content">
            <h4>Scholarship Drives</h4>
            <p>
              Fund-raising activities to support scholarships for deserving students.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      id="service-learning"
      class="content-section"
    >
      <h2>
        <i class="fas fa-chalkboard-teacher section-icon" />
        Service-Learning Programs
      </h2>
      <p>
        Service-learning integrates meaningful community service with instruction and reflection
        to enrich the learning experience, teach civic responsibility, and strengthen communities.
      </p>
      <div class="learning-features">
        <div class="feature">
          <div class="feature-number">
            01
          </div>
          <div class="feature-content">
            <h4>Course Integration</h4>
            <p>Service activities aligned with academic coursework and learning objectives.</p>
          </div>
        </div>
        <div class="feature">
          <div class="feature-number">
            02
          </div>
          <div class="feature-content">
            <h4>Community Partnership</h4>
            <p>Collaboration with local organizations, NGOs, and government agencies.</p>
          </div>
        </div>
        <div class="feature">
          <div class="feature-number">
            03
          </div>
          <div class="feature-content">
            <h4>Reflection Activities</h4>
            <p>Structured reflection to connect service experiences with learning outcomes.</p>
          </div>
        </div>
        <div class="feature">
          <div class="feature-number">
            04
          </div>
          <div class="feature-content">
            <h4>Skills Development</h4>
            <p>Practical application of academic knowledge in real-world settings.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-flag section-icon" />
        National Service Training Program (NSTP)
      </h2>
      <p>
        As mandated by RA 9163, all students are required to undergo the National Service
        Training Program. NSTP provides civic education and defense preparedness training
        to develop the ethics of service and patriotism.
      </p>
      <div class="nstp-options">
        <div class="nstp-card">
          <div class="nstp-icon">
            <i class="fas fa-shield-alt" />
          </div>
          <h4>Reserve Officers' Training Corps (ROTC)</h4>
          <p>Military training and civic welfare activities.</p>
        </div>
        <div class="nstp-card">
          <div class="nstp-icon">
            <i class="fas fa-book" />
          </div>
          <h4>Literacy Training Service (LTS)</h4>
          <p>Training students to become teachers of literacy and numeracy skills.</p>
        </div>
        <div class="nstp-card">
          <div class="nstp-icon">
            <i class="fas fa-hands-helping" />
          </div>
          <h4>Civic Welfare Training Service (CWTS)</h4>
          <p>Activities contributory to the general welfare and betterment of life.</p>
        </div>
      </div>
    </section>

    <section class="content-section stats">
      <h2>
        <i class="fas fa-chart-line section-icon" />
        Our Impact
      </h2>
      <div class="stats-grid">
        <div class="stat-item">
          <span class="stat-value">5,000+</span>
          <span class="stat-label">Volunteer Hours Annually</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">20+</span>
          <span class="stat-label">Partner Communities</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">1,500+</span>
          <span class="stat-label">Student Volunteers</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">50+</span>
          <span class="stat-label">Outreach Activities</span>
        </div>
      </div>
    </section>

    <section class="cta-section">
      <h2>Get Involved</h2>
      <p>Ready to make a difference? Join our community service programs today!</p>
      <button
        class="cta-button"
        @click="volunteerSignUp"
      >
        <i class="fas fa-hand-paper" />
        Sign Up to Volunteer
      </button>
    </section>

    <section class="content-section">
      <h2>
        <i class="fas fa-phone section-icon" />
        Contact Community Engagement
      </h2>
      <div class="contact-info">
        <div class="contact-item">
          <i class="fas fa-map-marker-alt" />
          <span>Room 205, Student Services Building</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-phone" />
          <span>(078) 123-4567 Local 205</span>
        </div>
        <div class="contact-item">
          <i class="fas fa-envelope" />
          <span>community@nc.edu.ph</span>
        </div>
      </div>
    </section>
  </ServicePageLayout>
</template>

<script>
import ServicePageLayout from '@/components/landing/ServicePageLayout.vue'

export default {
  name: 'CommunityService',

  components: {
    ServicePageLayout
  },

  metaInfo: () => ({
    title: 'Community & Social Involvement'
  }),

  methods: {
    volunteerSignUp () {
      this.$bvToast.toast('Volunteer sign-up coming soon!', {
        title: 'Coming Soon',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>
